import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarDays, CheckCircle2, Lock, RefreshCw, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { initiateGoogleCalendarConnection } from '@/lib/api'
import { BUTTON_STYLES } from '@/lib/buttonStyles'

const WHAT_YOU_GET = [
  {
    icon: CalendarDays,
    title: 'See everything in one place',
    description:
      'Your school, team, and personal events sit alongside NIL deadlines so you never double-book.',
  },
  {
    icon: RefreshCw,
    title: 'Always up to date',
    description:
      'Events sync automatically — add something to Google Calendar and it shows here instantly.',
  },
  {
    icon: Shield,
    title: 'Read-only access',
    description:
      "We only read your calendar events. We can't create, edit, or delete anything in your Google Calendar.",
  },
]

const GoogleCalendarConnect = () => {
  const navigate = useNavigate()
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConnect = async () => {
    setIsConnecting(true)
    setError(null)

    try {
      await initiateGoogleCalendarConnection()
    } catch (err: any) {
      console.error('Failed to initiate Google Calendar connection:', err)
      setError(err.message || 'Failed to connect Google Calendar. Please try again.')
      setIsConnecting(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 pb-10 pt-1">
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/calendar-setup')}
          className="h-auto w-fit p-0 text-[#99a1af] hover:bg-transparent hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Calendar Setup
        </Button>

        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold leading-9 text-foreground">Connect Google Calendar</h1>
          <p className="text-sm text-[#99a1af] sm:text-base">
            Sync your schedule with your NIL activity
          </p>
        </div>
      </div>

      {/* Google branding */}
      <div className="flex items-center gap-4 rounded-[18px] border border-[rgba(52,168,83,0.25)] bg-[rgba(52,168,83,0.08)] px-5 py-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] bg-linear-to-br from-[#00c950] to-[#009966] shadow-[0_10px_24px_rgba(0,201,80,0.25)]">
          <span className="text-2xl font-bold text-[#FFFFFF]">G</span>
        </div>
        <div>
          <p className="text-base font-semibold text-foreground">Google Calendar</p>
          <p className="mt-0.5 text-sm text-[#99a1af]">Google Workspace · School or personal</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-xs font-medium text-[#34a853]">
          <Lock className="h-3.5 w-3.5" />
          Secure
        </div>
      </div>

      {/* What you get */}
      <div className="rounded-2xl border border-border bg-card shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)]">
        <div className="border-b border-border px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#6a7282]">
            What you get
          </p>
        </div>
        <div className="divide-y divide-[#1e2939]">
          {WHAT_YOU_GET.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex items-start gap-4 px-5 py-4">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border border-[rgba(54,65,83,0.55)] bg-card">
                <Icon className="h-4 w-4 text-[#8ec5ff]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{title}</p>
                <p className="mt-0.5 text-sm leading-5 text-[#99a1af]">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Permissions note */}
      <div className="flex items-start gap-3 rounded-[14px] border border-[rgba(54,65,83,0.45)] bg-card px-4 py-3.5">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#34a853]" />
        <p className="text-sm leading-5 text-[#99a1af]">
          Google will ask you to confirm which calendars to share. You can choose to share all
          calendars or just specific ones — you're in control.
        </p>
      </div>

      {error ? (
        <div className="rounded-[14px] border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <Button
        onClick={handleConnect}
        disabled={isConnecting}
        className={`h-12 w-full rounded-[14px] text-base font-semibold ${BUTTON_STYLES.primary}`}
      >
        {isConnecting ? 'Redirecting to Google…' : 'Connect Google Calendar'}
      </Button>

      <p className="text-center text-xs text-[#6a7282]">
        You'll be taken to Google to sign in and approve access, then brought straight back.
      </p>
    </div>
  )
}

export default GoogleCalendarConnect
